import { PropsWithChildren, ReactNode, useCallback } from 'react';
import { useMediaQuery } from 'react-responsive';

import { minWidthQuery } from '~shared/lib/responsive';
import { useChatBottomOffset } from '~shared/lib/use-chat-bottom-offset';

import cn from 'classnames';
import { useSetAtom } from 'jotai';

import { screenAsideSlotAtom } from '../screen-aside-portal';

type Props = {
  bodyClassName?: string;
  header?: ReactNode;
  footer?: ReactNode;
  withoutPadding?: boolean;
} & PropsWithChildren;

const CHAT_SPACE = 80;
const MAX_CONTAINER_WIDTH = 480;

export const Screen = ({
  header,
  footer,
  children,
  withoutPadding,
  bodyClassName,
}: Props) => {
  const hasSideSpaceForChat = useMediaQuery({
    query: minWidthQuery(`${MAX_CONTAINER_WIDTH + CHAT_SPACE * 2}px`),
  });
  const footerRef = useChatBottomOffset<HTMLElement>({
    shouldApplyOffset: !hasSideSpaceForChat,
    bottomMargin: 0,
  });

  const setAsideSlot = useSetAtom(screenAsideSlotAtom);
  const asideSlotRef = useCallback(
    (element: HTMLElement | null) => setAsideSlot(element),
    [setAsideSlot]
  );

  return (
    <div className={cn('flex h-dvh min-h-dvh w-full flex-col')}>
      {header && <header>{header}</header>}
      <div
        className={`
          grid flex-1 scrollbar-gutter-both items-start overflow-y-auto
          lg:grid-cols-[240px_auto_240px]
        `}
      >
        <div
          ref={asideSlotRef}
          className={`
            sticky top-0 hidden shrink-0 self-start pl-6
            lg:block
            xl:pl-12
          `}
        />
        <div
          className={cn(
            `mx-auto flex min-h-full w-full max-w-lg flex-col px-4 pb-16`,
            {
              'px-0!': withoutPadding,
              'py-0!': withoutPadding,
            },
            bodyClassName
          )}
        >
          {children}
        </div>
        <div
          className={`
            hidden
            lg:block
          `}
        />
      </div>

      {footer && (
        <footer
          ref={footerRef}
          className="sticky right-0 bottom-0 left-0 z-10 bg-white"
        >
          {footer}
        </footer>
      )}
    </div>
  );
};
